import { Check, Copy } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { cx } from '@/shared/lib/cx';
import { springs } from '@/shared/lib/motion';
import { toast } from '@/shared/ui/Toast';
import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './CopyButton.module.scss';

export interface CopyButtonProps {
  /** Что копировать. */
  value: string;
  /** Подпись для скринридера и подсказки: «Скопировать ключ». */
  label?: string;
  className?: string;
}

/** Сколько держать галочку после копирования. */
const DONE_MS = 1600;

/**
 * Кнопка «скопировать» — для ключей задач, ссылок-приглашений, паролей из
 * блока «Доступы». Иконка резиново сменяется на галочку и возвращается.
 *
 * Ошибку копирования (нет разрешения, не HTTPS) показывает тостом: молча не
 * скопировать — худший исход, человек уже вставляет пустоту.
 */
export function CopyButton({ value, label = 'Скопировать', className }: CopyButtonProps) {
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!done) {
      return;
    }

    const timer = setTimeout(() => {
      setDone(false);
    }, DONE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [done]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
    } catch {
      toast.error('Не удалось скопировать', { description: 'Выделите текст и скопируйте вручную' });
    }
  };

  return (
    <Tooltip content={done ? 'Скопировано' : label}>
      <button
        type="button"
        className={cx(styles['copy'], done && styles['copy--done'], className)}
        aria-label={done ? 'Скопировано' : label}
        onClick={() => {
          void copy();
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={done ? 'done' : 'idle'}
            className={styles['copy__icon']}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.4, opacity: 0 }}
            transition={springs.rubber}
          >
            {done ? <Check /> : <Copy />}
          </motion.span>
        </AnimatePresence>
      </button>
    </Tooltip>
  );
}
